import { MutableRefObject } from 'react';
import { UseFormReturn } from 'react-hook-form';
import i18next from 'i18next';
import { Payouts } from '@airwallex/components-sdk';
import { PAYOUT_MINIMUM, WithdrawPayload } from '@monorepo/client-types';
import {
  FormCheckbox,
  List,
  ListItem,
  ListItemMeta,
  TextComponent,
} from '@monorepo/react-components';
import {
  getPayoutCalculation,
  getPayoutCommission,
  PaymentMethod,
  PayoutSource,
} from '@monorepo/types';
import AirwallexWithdraw from './withdraw-components/airwallex-withdraw';
import CryptoWithdraw from './withdraw-components/crypto-withdraw';
import RiseWithdraw from './withdraw-components/rise-withdraw';

export const formatter = (value: any) => {
  const numValue = typeof value === 'number' ? value : parseFloat(value) || 0;

  const formattedValue = Math.floor(numValue * 100) / 100;

  return `$ ${formattedValue}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export const parser = (value?: string) =>
  value?.replace(/\$\s?|(,*)/g, '') || '';

export const calculatePayout = (
  credits: number,
  selectedAmount: number,
  maxWithdrawal: number,
  userPayoutPercentage: number,
  source: PayoutSource,
) => {
  const { creditsToDeduct, userShare } = getPayoutCalculation({
    credits,
    amount: selectedAmount == null ? maxWithdrawal : selectedAmount,
    userPayoutPercentage,
    source: source,
  });
  const rawUserPayout = creditsToDeduct + userShare;
  const whatUserGets = Math.floor(rawUserPayout * 100) / 100;
  const minimumBalanceLegit = whatUserGets >= PAYOUT_MINIMUM;
  return { whatUserGets, minimumBalanceLegit };
};

type CheckboxKey = 'agreeToTerms' | 'acknowledgeWithdraw';

export const terms = (
  source: PayoutSource,
  form: UseFormReturn<WithdrawPayload>,
) => {
  const TERM_KEY = 'withdrawFunds:terms';

  const checkboxConfigs: Record<CheckboxKey, any> = {
    agreeToTerms: {
      text: `${TERM_KEY}.agree_to_the_withdrawal_terms`,
      name: 'agreedToTerms',
      required: `${TERM_KEY}.this_field_is_required`,
    },
    acknowledgeWithdraw: {
      text: `${TERM_KEY}.request_payout_will_be_withdrawn_from_your_trading_account`,
      name: 'acknowledgeWithdrawFormAccount',
      required: `${TERM_KEY}.acknowledgement_in_withdraw_terms_is_required`,
    },
  };

  const sourceCheckboxes: Record<PayoutSource, CheckboxKey[]> = {
    [PayoutSource.Account]: ['agreeToTerms', 'acknowledgeWithdraw'],
    [PayoutSource.Affiliate]: ['agreeToTerms'],
  };

  return sourceCheckboxes[source].map((key) => {
    const config = checkboxConfigs[key];

    const checkboxControllerProps = {
      name: config.name,
      rules: {
        required: i18next.t(config.required),
      },
    };

    return (
      <FormCheckbox
        key={config.name}
        checkboxText={i18next.t(config.text)}
        form={form}
        controllerProps={checkboxControllerProps}
      />
    );
  });
};

export const notes = (source: PayoutSource, paymentMethod: PaymentMethod) => {
  const NOTE_KEY = 'withdrawFunds:notes';

  const listItems = {
    [PayoutSource.Account]: [
      i18next.t(`${NOTE_KEY}.payout_minimum`, {
        payoutMinimum: PAYOUT_MINIMUM,
      }),
      i18next.t(`${NOTE_KEY}.payout_delay`),
      i18next.t(`${NOTE_KEY}.payout_non_reversible`),
    ],
    [PayoutSource.Affiliate]: [
      i18next.t(`${NOTE_KEY}.payout_minimum_affiliate`, {
        payoutMinimum: PAYOUT_MINIMUM,
      }),
      i18next.t(`${NOTE_KEY}.payout_delay`),
      i18next.t(`${NOTE_KEY}.payout_non_reversible`),
    ],
  };

  const commissionPercentage = getPayoutCommission({
    paymentMethod,
  });

  if (commissionPercentage) {
    listItems[source].push(
      i18next.t(`${NOTE_KEY}.clearing_fee`, {
        commissionPercentage,
      }),
    );
  }

  return (
    <List
      split={false}
      dataSource={listItems[source]}
      renderItem={(item) => (
        <ListItem style={{ border: 'none', padding: '2px 0' }}>
          <ListItemMeta
            avatar={<TextComponent level={5}>•</TextComponent>}
            description={<TextComponent level={7}>{item}</TextComponent>}
          />
        </ListItem>
      )}
    />
  );
};

export const getPaymentForm = (
  selectedPaymentOption: PaymentMethod,
  form: UseFormReturn<WithdrawPayload>,
  beneficiaryFormRef: MutableRefObject<
    Payouts.BeneficiaryFormElement | undefined
  >,
) => {
  if (!selectedPaymentOption) {
    return null;
  }

  const formsMapping = {
    [PaymentMethod.Rise]: <RiseWithdraw form={form} />,
    [PaymentMethod.Crypto]: <CryptoWithdraw form={form} />,
    [PaymentMethod.CreditCard]: null,
    [PaymentMethod.HubCredits]: null,
    [PaymentMethod.Wise]: null,
    [PaymentMethod.Airwallex]: (
      <AirwallexWithdraw beneficiaryFormRef={beneficiaryFormRef} />
    ),
  };

  return formsMapping[selectedPaymentOption];
};
